import Dexie from 'dexie';

export const db = new Dexie('MICUMedicMobile');

db.version(1).stores({
  cases: '++id, name, createdAt, updatedAt, syncStatus, remoteId',
  patients: '++id, caseId, gender, age, ageUnit, heightInches, weightKg, ibwKg',
  devices: '++id, caseId, deviceType, addedAt, syncStatus, remoteId',
  deviceLogs: '++id, deviceId, logType, timestamp, data',
  medications: '++id, caseId, drugName, templateId, concentration, concentrationUnit, dose, doseUnit, rate, rateUnit, vtbi, alarmMinutes, startedAt, syncStatus, remoteId',
  medicationLogs: '++id, medicationId, timestamp, field, oldValue, newValue',
  syncQueue: '++id, table, recordId, action, payload, createdAt',
  auth: '++id, key, value',
});

// Helper: create a case
export async function createCase(name) {
  const now = new Date().toISOString();
  const id = await db.cases.add({
    name,
    createdAt: now,
    updatedAt: now,
    syncStatus: 'local',
    remoteId: null,
  });
  await db.syncQueue.add({
    table: 'cases',
    recordId: id,
    action: 'create',
    payload: { name, createdAt: now },
    createdAt: now,
  });
  return id;
}

// Helper: update a case
export async function updateCase(id, updates) {
  const now = new Date().toISOString();
  await db.cases.update(id, { ...updates, updatedAt: now, syncStatus: 'local' });
  await db.syncQueue.add({
    table: 'cases',
    recordId: id,
    action: 'update',
    payload: updates,
    createdAt: now,
  });
}

// Helper: delete a case and all related records
export async function deleteCase(id) {
  const now = new Date().toISOString();
  const devices = await db.devices.where('caseId').equals(id).toArray();
  const meds = await db.medications.where('caseId').equals(id).toArray();

  for (const dev of devices) {
    await db.deviceLogs.where('deviceId').equals(dev.id).delete();
  }
  for (const med of meds) {
    await db.medicationLogs.where('medicationId').equals(med.id).delete();
  }

  await db.devices.where('caseId').equals(id).delete();
  await db.medications.where('caseId').equals(id).delete();
  await db.patients.where('caseId').equals(id).delete();
  await db.cases.delete(id);

  await db.syncQueue.add({
    table: 'cases',
    recordId: id,
    action: 'delete',
    payload: null,
    createdAt: now,
  });
}

// Helper: save/update patient demographics
export async function savePatient(caseId, data) {
  const existing = await db.patients.where('caseId').equals(caseId).first();
  if (existing) {
    await db.patients.update(existing.id, data);
    return existing.id;
  }
  return db.patients.add({ caseId, ...data });
}

// Helper: get patient for a case
export async function getPatient(caseId) {
  return db.patients.where('caseId').equals(caseId).first();
}

// Helper: add a device to a case
export async function addDevice(caseId, deviceType) {
  const now = new Date().toISOString();
  const id = await db.devices.add({
    caseId,
    deviceType,
    addedAt: now,
    syncStatus: 'local',
    remoteId: null,
  });
  await db.syncQueue.add({
    table: 'devices',
    recordId: id,
    action: 'create',
    payload: { caseId, deviceType, addedAt: now },
    createdAt: now,
  });
  return id;
}

// Helper: log a device setting or measurement change
export async function logDeviceChange(deviceId, logType, data) {
  const now = new Date().toISOString();
  return db.deviceLogs.add({ deviceId, logType, timestamp: now, data: JSON.stringify(data) });
}

// Helper: add a medication to a case
export async function addMedication(caseId, medData) {
  const now = new Date().toISOString();
  const id = await db.medications.add({
    caseId,
    ...medData,
    startedAt: now,
    syncStatus: 'local',
    remoteId: null,
  });
  await db.syncQueue.add({
    table: 'medications',
    recordId: id,
    action: 'create',
    payload: { caseId, ...medData, startedAt: now },
    createdAt: now,
  });
  return id;
}

// Helper: update medication fields and log change
export async function updateMedication(id, field, oldValue, newValue) {
  const now = new Date().toISOString();
  await db.medications.update(id, { [field]: newValue, syncStatus: 'local' });
  await db.medicationLogs.add({ medicationId: id, timestamp: now, field, oldValue: String(oldValue), newValue: String(newValue) });
  await db.syncQueue.add({
    table: 'medications',
    recordId: id,
    action: 'update',
    payload: { [field]: newValue },
    createdAt: now,
  });
}

// Helper: delete a medication
export async function deleteMedication(id) {
  const now = new Date().toISOString();
  await db.medicationLogs.where('medicationId').equals(id).delete();
  await db.medications.delete(id);
  await db.syncQueue.add({
    table: 'medications',
    recordId: id,
    action: 'delete',
    payload: null,
    createdAt: now,
  });
}

// Helper: delete a device
export async function deleteDevice(id) {
  const now = new Date().toISOString();
  await db.deviceLogs.where('deviceId').equals(id).delete();
  await db.devices.delete(id);
  await db.syncQueue.add({
    table: 'devices',
    recordId: id,
    action: 'delete',
    payload: null,
    createdAt: now,
  });
}

// Reset database (for dev/testing)
export async function resetDatabase() {
  await db.delete();
  await db.open();
}
